import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppFloat } from '../components/WhatsAppFloat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Lifestyle', 'Diet', 'Seasonal', 'Treatments'];

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        await axios.post(`${API}/seed-articles`);
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const filteredArticles = selectedCategory === 'All'
    ? articles
    : articles.filter(a => a.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="articles-page" className="min-h-screen bg-[#F9F8F6]">
      <div className="grain-overlay" />
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="section-container">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#6B6B6B] hover:text-[#2D4A3E] mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <span className="caption mb-4 block">Ayurveda Knowledge</span>
            <h1 className="heading-1 text-[#2D4A3E] mb-4" data-testid="articles-page-heading">
              Health & Wellness Articles
            </h1>
            <p className="body-large text-[#6B6B6B] max-w-2xl">
              Explore our collection of articles on Ayurvedic wisdom, healthy
              living, and natural remedies for a balanced life.
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-[#2D4A3E] text-white'
                    : 'bg-white text-[#6B6B6B] hover:bg-[#E6E2D8]'
                }`}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Articles Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl h-48 mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                  <div className="h-6 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-[#6B6B6B]">
              <p>No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="article-card group"
                  data-testid={`article-${article.slug}`}
                >
                  <Link to={`/articles/${article.slug}`}>
                    <div className="relative rounded-2xl overflow-hidden mb-4">
                      <img
                        src={article.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800'}
                        alt={article.title}
                        className="article-image w-full h-48"
                      />
                    </div>

                    <div className="flex items-center gap-4 mb-3 text-sm text-[#6B6B6B]">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3.5 h-3.5" />
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>

                    <h2 className="heading-3 text-[#2D4A3E] mb-2 group-hover:text-[#C67D63] transition-colors duration-200">
                      {article.title}
                    </h2>
                    <p className="text-[#6B6B6B] leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>

                    <span className="inline-flex items-center gap-2 mt-4 text-[#C67D63] font-medium">
                      Read More
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
